import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { getLatestComments } from '../commentService';
import { formatRelativeTime } from '../utils/timeUtils';
import { buildAddressUrl, buildCoinUrl } from '../urlUtils';

/**
 * LatestComments - Displays the most recent comments as a horizontal list
 * Shows above search boxes on homepage, coin page, and address page
 */
const LatestComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestComments = async () => {
      try {
        setLoading(true);
        const latestComments = await getLatestComments(6);
        setComments(latestComments);
        setError(null);
      } catch (err) {
        console.error('Error fetching latest comments:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestComments();
  }, []);

  // Don't render anything while loading or if there's an error or no comments
  if (loading || error || comments.length === 0) {
    return null;
  }

  const getCommentUrl = (comment) => {
    const highlightId = comment.id;

    if (comment.targetType === 'coin') {
      return buildCoinUrl(comment.targetCoinId, highlightId);
    } else {
      const chain = comment.targetChain || comment.chain || 'ethereum';
      return buildAddressUrl(comment.targetAddress, chain, highlightId);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-5 h-5 text-blue-600" />
        <span className="text-base font-semibold text-gray-800">Latest Comments</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm">
        {comments.map((comment, index) => (
          <div key={comment.id} className="flex items-center gap-1 min-w-0">
            <span className="text-gray-400 flex-shrink-0">{index + 1}.</span>
            <Link
              to={getCommentUrl(comment)}
              className="flex-1 text-gray-700 hover:text-blue-600 transition-colors text-left truncate min-w-0"
              title={comment.text}
            >
              {comment.text}
            </Link>
            <span className="text-gray-400 text-xs flex-shrink-0">
              {formatRelativeTime(comment.createdAt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LatestComments;
